import { useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';

/** Material-UI */
import { useTheme } from '@mui/material';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';

/* Layout */
import { FullWidthLayout } from 'layouts';

/** Components */
import CustomTabs from 'components/CustomTabs';
import NewInPageSeo from 'components/Seo/NewInPageSeo';
import VirgoFilters, { VIRGO_FILTERS } from 'components/VirgoFilters';
import BrandNewInTabContent from 'components/Brand/BrandNewInTabContent';
import ProductNewInTabContent from 'components/Product/ProductNewInTabContent';

/** Utils / Consts */
import routes from 'constants/routes';
import { getSSRInitialData } from 'utils/SSR';
import { filterUrlQueryParams } from 'constants/category-filters';
import { getApiQueryParamsFromUrlQueryParams } from 'utils/functions';
import {
  DEFAULT_BRANDS_LIMIT,
  DEFAULT_LIMITS,
  DEFAULT_PRODUCTS_LIMIT,
  DEFAULT_TAB_LISTING_PAGES,
} from 'constants/listing-defaults';

// services
import { logger } from 'services/api/logger';
import FiltersProvider from 'provider/FiltersProvider';

// Hooks
import useResponsive from 'hooks/useResponsive';
import { usePreserveScroll } from 'hooks/usePreserveScroll';

const newInDefaultPropInitialProductsData = {
  totalProducts: 0,
  products: [],
};

const newInDefaultPropInitialBrandsData = {
  totalBrands: 0,
  brands: [],
};

const newInDefaultPropInitialQueryParams = {};

const NewIn = ({
  initialProductsData = newInDefaultPropInitialProductsData,
  initialBrandsData = newInDefaultPropInitialBrandsData,
  initialQueryParams = newInDefaultPropInitialQueryParams,
  initialTab = DEFAULT_TAB_LISTING_PAGES,
}) => {
  const theme = useTheme();
  const router = useRouter();
  const { query } = router;

  const isTablet = useResponsive('down', 'md');
  const isDesktop = useResponsive('up', 'lg');

  /** Query Params */
  const { itemId, updateItemId } = usePreserveScroll(filterUrlQueryParams.ITEM_ID);

  const currentTab = query?.tab || initialTab;

  const [limit, setLimit] = useState({
    brands: (currentTab === 'brands' && Number(query.limit)) || DEFAULT_BRANDS_LIMIT,
    products: (currentTab === 'products' && Number(query.limit)) || DEFAULT_PRODUCTS_LIMIT,
  });

  const queryParams = {
    itemId,
    queryLimit: limit,
  };

  const handleLimitChange = () => {
    setLimit((prevState) => {
      router.push(
        {
          ...router,
          query: {
            ...router.query,
            limit: Number(prevState[currentTab]) + DEFAULT_LIMITS[currentTab],
          },
        },
        undefined,
        { shallow: true }
      );

      return {
        ...prevState,
        [currentTab]: Number(prevState[currentTab]) + DEFAULT_LIMITS[currentTab],
      };
    });
  };

  const handleItemIdChange = (newItemId, resetItemId = false) => {
    if (!newItemId && !resetItemId) {
      return;
    }
    updateItemId(newItemId);
  };

  const handleTabChange = () => {
    handleItemIdChange('');
  };

  const queryParametersHandlers = {
    handleLimitChange,
    handleItemIdChange,
  };

  const filtersToBeApplied = [
    { id: VIRGO_FILTERS.COUNTRIES },
    { id: VIRGO_FILTERS.CATEGORIES },
    { id: VIRGO_FILTERS.MIN_ORDER },
    { id: VIRGO_FILTERS.LEAD_TIME_MAX_DAYS },
    { id: VIRGO_FILTERS.BRAND_VALUE },
    { id: VIRGO_FILTERS.FLAIRS },
    { id: VIRGO_FILTERS.ON_OFFER },
  ];

  const defaultTabIndex = ['brands', 'products'].findIndex((tab) => tab === currentTab);

  return (
    <>
      <NewInPageSeo products={initialProductsData?.products.slice(0, 5)} />
      <FiltersProvider initialQueryParams={initialQueryParams}>
        {/* Filters */}
        {isTablet && <VirgoFilters sx={{ mt: -2 }} filters={filtersToBeApplied} defaultTab={currentTab} />}
        <Container
          maxWidth={false}
          sx={{
            py: 1.25,
            [theme.breakpoints.down('md')]: {
              px: theme.spacing(1),
            },
          }}
        >
          {/* Header Text */}
          <Typography component="h1" variant="h4" gutterBottom sx={{ textAlign: 'center' }}>
            New-in Wholesale Products
          </Typography>

          {isDesktop && <VirgoFilters filters={filtersToBeApplied} defaultTab={currentTab} />}

          {/* Tabs to select between Products and Brands */}
          <CustomTabs
            tabs={[
              {
                tabName: 'brands',
                tabContent: (
                  <BrandNewInTabContent
                    initialBrandsData={initialBrandsData}
                    {...queryParametersHandlers}
                    {...queryParams}
                  />
                ),
              },
              {
                tabName: 'products',
                tabContent: (
                  <ProductNewInTabContent
                    initialProductsData={initialProductsData}
                    {...queryParametersHandlers}
                    {...queryParams}
                  />
                ),
              },
            ]}
            config={{
              label: 'new-in',
              defaultTabIndex: {
                authenticated: defaultTabIndex,
                nonAuthenticated: defaultTabIndex,
              },
            }}
            sx={isTablet ? { mb: 2 } : { my: 2 }}
            onTabChange={handleTabChange}
          />
        </Container>
      </FiltersProvider>
    </>
  );
};

NewIn.getLayout = FullWidthLayout;

NewIn.propTypes = {
  initialProductsData: PropTypes.shape({
    totalProducts: PropTypes.number,
    products: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  initialBrandsData: PropTypes.shape({
    totalProducts: PropTypes.number,
    products: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  initialQueryParams: PropTypes.shape({}),
  initialTab: PropTypes.string,
};

export const getServerSideProps = async ({ query, req }) => {
  const { fetcher, userCountry, queryParams } = await getSSRInitialData({ query, req, isPublic: true });

  const productsQueryParams = getApiQueryParamsFromUrlQueryParams(
    {
      ...queryParams,
      limit: queryParams.tab === 'products' ? queryParams.limit : DEFAULT_PRODUCTS_LIMIT,
      'sort-by': 'latest', // hardcoded for new-in page only
    },
    { defaults: { sortBy: 'latest' } }
  );

  const userCountryShippingCode = userCountry ? `shippingCountryCode=${userCountry}` : '';
  const getNewInEndpoint = (resource, params) => `/${resource}?${userCountryShippingCode}${params}`;

  const productsEndpoint = getNewInEndpoint('products', productsQueryParams);

  const allPromises = [];

  allPromises.push(fetcher.get(productsEndpoint));

  const brandQueryParams = getApiQueryParamsFromUrlQueryParams(
    {
      ...queryParams,
      limit: queryParams.tab === 'brands' ? queryParams.limit : DEFAULT_BRANDS_LIMIT,
      'sort-by': 'latest', // hardcoded for new-in page only
    },
    { defaults: { sortBy: 'latest' } }
  );
  const brandsEndpoint = getNewInEndpoint('brands', brandQueryParams);
  allPromises.push(fetcher.get(brandsEndpoint));

  try {
    const [productsResponse, brandsResponse] = await Promise.all(allPromises);
    return {
      props: {
        initialQueryParams: queryParams || {},
        initialProductsData: productsResponse?.data || [],
        initialBrandsData: brandsResponse?.data || [],
        initialTab: query?.tab || DEFAULT_TAB_LISTING_PAGES,
      },
    };
  } catch (err) {
    logger.error({ instance: 'new-in', type: 'getServerSideProps' }, err);

    if (err.response?.status === 404) {
      return {
        notFound: true,
      };
    }

    const isForbiddenUnauthorized = err.response?.status === 401 || err.response?.status === 403;

    return {
      redirect: {
        permanent: false,
        destination: isForbiddenUnauthorized ? routes.auth.logout : routes.page.root,
      },
    };
  }
};

export default NewIn;
